import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  agentPrepare,
  createDraft,
  deleteDraft,
  fetchReplyContext,
  getDraft,
  listAccounts,
  sendMessage,
  getSendStatus,
  updateDraft,
  checkLanguageQualityDraft,
  type MailAccount,
  type SendResult,
} from '../lib/api'
import { Icon } from './Icon'

export type ComposeMode = 'new' | 'reply' | 'replyAll' | 'forward'

export type ComposeSeed = {
  mode: ComposeMode
  messageId?: string
  accountId?: string
  to?: string
  subject?: string
  body?: string
  /** Server-side draft id (Skice) — never localStorage */
  draftId?: string
}

type Props = {
  open: boolean
  seed: ComposeSeed | null
  onClose: () => void
  onSent?: () => void
  onQueued?: (info: { id: string; undoUntil: number }) => void
  onDraftSaved?: () => void
}

type Phase = 'edit' | 'approve'

/** Drop legacy client drafts (XSS / no-localStorage policy). */
function purgeLegacyDraftStorage() {
  try {
    localStorage.removeItem('7gc-mail-drafts')
  } catch {
    /* ignore */
  }
}

export function ComposeModal({ open, seed, onClose, onSent, onQueued, onDraftSaved }: Props) {
  const { t } = useTranslation()
  const [tab, setTab] = useState<'classic' | 'intent'>('classic')
  const [phase, setPhase] = useState<Phase>('edit')
  const [fromId, setFromId] = useState('')
  const [to, setTo] = useState('')
  const [cc, setCc] = useState('')
  const [showCc, setShowCc] = useState(false)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [replyMsgId, setReplyMsgId] = useState<string | null>(null)
  const [inReplyHeader, setInReplyHeader] = useState<string | null>(null)
  const [references, setReferences] = useState<string | null>(null)
  const [draftId, setDraftId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [files, setFiles] = useState<File[]>([])
  const [delaySec, setDelaySec] = useState(15)
  const [intent, setIntent] = useState('')
  const [arrivedOn, setArrivedOn] = useState<string | null>(null)
  const [uncertain, setUncertain] = useState<string | null>(null)
  const [lqgOriginal, setLqgOriginal] = useState('')
  const [lqgSummary, setLqgSummary] = useState<string | null>(null)
  const [lqgOk, setLqgOk] = useState(true)
  const fileRef = useRef<HTMLInputElement | null>(null)

  const accountsQ = useQuery({ queryKey: ['accounts'], queryFn: listAccounts, enabled: open })
  const accounts = accountsQ.data?.items || []

  const fromAccount = accounts.find((a) => a.id === fromId)
  const identityMismatch = useMemo(() => {
    if (!arrivedOn || !fromId) return false
    return (fromAccount?.username || '').toLowerCase() !== arrivedOn.toLowerCase()
  }, [arrivedOn, fromId, fromAccount])

  const externalRisk = useMemo(() => {
    const domains = (to + ',' + cc)
      .split(/[,;]/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => {
        const m = s.match(/@([^\s>]+)/)
        return (m?.[1] || '').toLowerCase()
      })
    const mine = (fromAccount?.username || '').split('@')[1]?.toLowerCase() || ''
    return domains.some((d) => d && d !== mine)
  }, [to, cc, fromAccount])

  useEffect(() => {
    if (!open || !seed) return
    purgeLegacyDraftStorage()
    setTab('classic')
    setPhase('edit')
    setError(null)
    setNote(null)
    setFiles([])
    setDelaySec(15)
    setUncertain(null)
    setLqgOriginal('')
    setLqgSummary(null)
    setLqgOk(true)
    setShowCc(false)
    setCc('')
    setReplyMsgId(null)
    setInReplyHeader(null)
    setReferences(null)
    setArrivedOn(null)
    setDraftId(seed.draftId || null)

    if (seed.draftId) {
      let cancelled = false
      void (async () => {
        try {
          const d = await getDraft(seed.draftId!)
          if (cancelled) return
          setDraftId(d.id)
          setFromId(d.account_id || seed.accountId || accounts[0]?.id || '')
          setTo(d.to_addrs || '')
          setSubject(d.subject || '')
          setBody(d.body_text || '')
          if (d.cc_addrs) {
            setCc(d.cc_addrs)
            setShowCc(true)
          }
          setReplyMsgId(d.in_reply_to_message_id)
          setInReplyHeader(d.in_reply_to_header)
          setReferences(d.references_header)
        } catch (e) {
          if (!cancelled) setError(e instanceof Error ? e.message : t('compose.draftLoadFailed'))
        }
      })()
      return () => {
        cancelled = true
      }
    }

    if (seed.mode === 'new') {
      const aid = seed.accountId || accounts[0]?.id || ''
      setFromId(aid)
      setTo(seed.to || '')
      setSubject(seed.subject || '')
      setBody(seed.body || '')
      return
    }
    if (!seed.messageId) return
    let cancelled = false
    void (async () => {
      try {
        const ctx = await fetchReplyContext(seed.messageId!)
        if (cancelled) return
        setFromId(seed.accountId || ctx.from_account_id)
        setArrivedOn(ctx.from_account_email)
        setReplyMsgId(ctx.message_id)
        setInReplyHeader(ctx.in_reply_to)
        setReferences(ctx.references)
        if (seed.mode === 'forward') {
          setTo('')
          setSubject(ctx.subject.startsWith('Fwd:') ? ctx.subject : `Fwd: ${ctx.subject}`)
          setBody(ctx.quoted_text)
          setReplyMsgId(null)
          return
        }
        if (seed.mode === 'replyAll') {
          setTo(ctx.to_all.join(', '))
          setCc(ctx.cc.join(', '))
          setShowCc(ctx.cc.length > 0)
        } else {
          setTo(ctx.to.join(', '))
        }
        setSubject(ctx.subject)
        setBody(`\n${ctx.quoted_text}`)
        setLqgOriginal(ctx.source_body_text || ctx.quoted_text || '')
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : t('compose.sendFailed'))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, seed?.mode, seed?.messageId, seed?.accountId, seed?.draftId])

  useEffect(() => {
    if (!open) return
    if (!fromId && accounts[0]) setFromId(accounts[0].id)
  }, [open, fromId, accounts])

  if (!open) return null

  async function doSend() {
    setError(null)
    if (!fromId) {
      setError(t('compose.needAccount'))
      return
    }
    if (!to.trim()) {
      setError(t('compose.needTo'))
      return
    }
    setBusy(true)
    const idem = crypto.randomUUID()
    const delay = files.length ? 0 : delaySec
    try {
      const res: SendResult = await sendMessage({
        from_account_id: fromId,
        to: to.trim(),
        cc: showCc ? cc.trim() : '',
        subject,
        body_text: body,
        in_reply_to_message_id: replyMsgId,
        in_reply_to_header: inReplyHeader,
        references_header: references,
        files,
        delay_seconds: delay,
        idempotency_key: idem,
      })
      if (res.status === 'queued' || res.status === 'sent') {
        if (draftId) {
          void deleteDraft(draftId).catch(() => undefined)
          setDraftId(null)
          onDraftSaved?.()
        }
      }
      if (res.status === 'queued') {
        const until = res.undo_until ? Date.parse(res.undo_until) : Date.now() + (res.undo_seconds || 15) * 1000
        onQueued?.({ id: res.id, undoUntil: until })
        onSent?.()
        setPhase('edit')
        onClose()
      } else if (res.status === 'uncertain') {
        setUncertain(res.message || t('compose.uncertain'))
        void pollUncertain(res.id)
      } else if (res.status === 'sent') {
        onSent?.()
        onClose()
      } else {
        setError(t('compose.sendFailed'))
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('compose.sendFailed'))
    } finally {
      setBusy(false)
    }
  }

  async function pollUncertain(id: string) {
    for (let i = 0; i < 10; i++) {
      await new Promise((r) => setTimeout(r, 1500))
      try {
        const st = await getSendStatus(id)
        if (st.status === 'sent') {
          setUncertain(null)
          onSent?.()
          onClose()
          return
        }
        if (st.status === 'failed') {
          setUncertain(null)
          setError(st.error || t('compose.sendFailed'))
          return
        }
      } catch {
        /* keep polling */
      }
    }
  }

  async function onPrepareIntent() {
    setBusy(true)
    setError(null)
    try {
      const res = await agentPrepare({
        intent,
        message_id: seed?.messageId,
      })
      if (!res.ok) {
        setError(res.why || t('compose.sendFailed'))
        return
      }
      if (res.subject) setSubject(res.subject)
      setBody(res.draft)
      setTab('classic')
    } catch (e) {
      setError(e instanceof Error ? e.message : t('compose.sendFailed'))
    } finally {
      setBusy(false)
    }
  }

  const title =
    seed?.mode === 'reply' || seed?.mode === 'replyAll'
      ? t('compose.replyTitle')
      : seed?.mode === 'forward'
        ? t('compose.forwardTitle')
        : t('compose.title')

  return (
    <>
      <div className="mail-ovl" role="dialog" aria-modal="true" aria-label={title}>
        <div className="mail-modal mail-compose-modal">
          <div className="mail-m-head">
            <b>{phase === 'approve' ? t('compose.approveTitle') : title}</b>
            <button type="button" className="mail-m-x" onClick={onClose} aria-label={t('auth.cancel')}>
              <Icon name="close" size={18} />
            </button>
          </div>
          <div className="mail-m-body">
            {phase === 'approve' ? (
              <>
                {externalRisk ? <span className="mail-ap-risk">{t('compose.riskExternal')}</span> : null}
                <dl className="mail-approve-grid">
                  <dt>{t('compose.from')}</dt>
                  <dd>{fromAccount?.username}</dd>
                  <dt>{t('compose.to')}</dt>
                  <dd>{to}</dd>
                  {showCc && cc ? (
                    <>
                      <dt>Cc</dt>
                      <dd>{cc}</dd>
                    </>
                  ) : null}
                  <dt>{t('compose.subject')}</dt>
                  <dd>{subject}</dd>
                  <dt>{t('compose.attachments')}</dt>
                  <dd>{files.length ? files.map((f) => f.name).join(', ') : t('compose.none')}</dd>
                  <dt>{t('compose.delay')}</dt>
                  <dd>{files.length ? t('compose.immediate') : `${delaySec}s`}</dd>
                </dl>
                <div className="mail-langline">
                  <Icon name="translate" size={16} />
                  <span>{t('compose.langNote')}</span>
                </div>
                {lqgSummary ? (
                  <div className={`mail-idg${lqgOk ? '' : ' warn'}`} role="status">
                    <Icon name="info" size={16} />
                    <span>{lqgSummary}</span>
                  </div>
                ) : null}
                {identityMismatch && arrivedOn ? (
                  <div className="mail-idg" role="status">
                    <Icon name="info" size={16} />
                    <span>{t('compose.identityGuard', { email: arrivedOn })}</span>
                  </div>
                ) : null}
                <pre
                  style={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'inherit',
                    fontSize: '0.88em',
                    maxHeight: 160,
                    overflow: 'auto',
                    background: 'var(--m-surface-2)',
                    padding: 12,
                    borderRadius: 12,
                  }}
                >
                  {body}
                </pre>
                {error ? (
                  <p className="mail-set-note" role="alert" style={{ color: 'var(--status-danger-text)' }}>
                    {error}
                  </p>
                ) : null}
                {uncertain ? <p className="mail-set-note">{uncertain}</p> : null}
                <div className="mail-c-foot">
                  <button type="button" className="mail-send" disabled={busy || identityMismatch} onClick={() => void doSend()}>
                    <Icon name="check" size={16} /> {busy ? t('compose.sending') : t('compose.confirmSend')}
                  </button>
                  <button type="button" className="mail-tbtn" onClick={() => setPhase('edit')}>
                    {t('compose.edit')}
                  </button>
                  <button type="button" className="mail-tbtn" onClick={onClose}>
                    {t('auth.cancel')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="mail-c-tabs">
                  <button type="button" className={tab === 'classic' ? 'on' : ''} onClick={() => setTab('classic')}>
                    {t('compose.tabClassic')}
                  </button>
                  <button type="button" className={tab === 'intent' ? 'on' : ''} onClick={() => setTab('intent')}>
                    <Icon name="auto_awesome" size={16} /> {t('compose.tabIntent')}
                  </button>
                </div>
                {tab === 'intent' ? (
                  <div>
                    <p className="mail-set-note">{t('compose.intentHint')}</p>
                    <textarea className="mail-intent-box" value={intent} onChange={(e) => setIntent(e.target.value)} rows={5} />
                    <button type="button" className="mail-tbtn ai" style={{ marginTop: 10 }} disabled={busy || !intent.trim()} onClick={() => void onPrepareIntent()}>
                      <Icon name="auto_awesome" size={16} /> {t('compose.prepareDraft')}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="mail-frow">
                      <label>{t('compose.from')}</label>
                      <select value={fromId} onChange={(e) => setFromId(e.target.value)}>
                        {accounts.map((a: MailAccount) => (
                          <option key={a.id} value={a.id}>
                            {(a.label || a.provider || 'mail') + ' — ' + a.username}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mail-frow">
                      <label>{t('compose.to')}</label>
                      <input value={to} onChange={(e) => setTo(e.target.value)} />
                      {!showCc ? (
                        <button type="button" className="mail-linkish" onClick={() => setShowCc(true)}>
                          Cc
                        </button>
                      ) : null}
                    </div>
                    {showCc ? (
                      <div className="mail-frow">
                        <label>Cc</label>
                        <input value={cc} onChange={(e) => setCc(e.target.value)} />
                      </div>
                    ) : null}
                    <div className="mail-frow">
                      <label>{t('compose.subject')}</label>
                      <input value={subject} onChange={(e) => setSubject(e.target.value)} />
                    </div>
                    {identityMismatch && arrivedOn ? (
                      <div className="mail-idg" role="status">
                        <Icon name="info" size={16} />
                        <span>{t('compose.identityGuard', { email: arrivedOn })}</span>
                        <button
                          type="button"
                          onClick={() => {
                            const m = accounts.find((a) => (a.username || '').toLowerCase() === arrivedOn.toLowerCase())
                            if (m) setFromId(m.id)
                          }}
                        >
                          {t('compose.switchAccount')}
                        </button>
                      </div>
                    ) : null}
                    <textarea className="mail-c-body" value={body} onChange={(e) => setBody(e.target.value)} rows={10} />
                    <div className="mail-frow mail-attach-row" style={{ border: 'none' }}>
                      <label>{t('compose.attachments')}</label>
                      <div className="mail-attach-ctrl">
                        <input
                          ref={fileRef}
                          id="mail-compose-files"
                          type="file"
                          multiple
                          className="mail-file-native"
                          onChange={(e) => setFiles(Array.from(e.target.files || []))}
                        />
                        <button
                          type="button"
                          className="mail-tbtn mail-attach-btn"
                          onClick={() => fileRef.current?.click()}
                        >
                          <Icon name="attach_file" size={16} /> {t('compose.addAttachment')}
                        </button>
                      </div>
                    </div>
                    {files.length ? (
                      <p className="mail-set-note">{files.map((f) => f.name).join(', ')}</p>
                    ) : null}
                    <div className="mail-frow" style={{ border: 'none' }}>
                      <label>{t('compose.delay')}</label>
                      <select value={delaySec} onChange={(e) => setDelaySec(Number(e.target.value))} disabled={files.length > 0}>
                        <option value={15}>15s (Undo)</option>
                        <option value={60}>60s</option>
                        <option value={120}>120s</option>
                        <option value={0}>{t('compose.immediate')}</option>
                      </select>
                    </div>
                    {error ? (
                      <p className="mail-set-note" role="alert" style={{ color: 'var(--status-danger-text)' }}>
                        {error}
                      </p>
                    ) : null}
                    {note && !error ? (
                      <p className="mail-set-note" role="status">
                        {note}
                      </p>
                    ) : null}
                    <div className="mail-c-foot">
                      <button
                        type="button"
                        className="mail-send"
                        disabled={busy}
                        onClick={() => {
                          if (identityMismatch) {
                            setError(t('compose.identityBlock'))
                            return
                          }
                          void (async () => {
                            setLqgSummary(null)
                            setLqgOk(true)
                            if (lqgOriginal.trim()) {
                              try {
                                const q = await checkLanguageQualityDraft({
                                  original: lqgOriginal,
                                  candidate: body,
                                })
                                setLqgSummary(q.summary)
                                setLqgOk(q.ok)
                                if (!q.ok) {
                                  setError(t('compose.lqgWarn'))
                                  setPhase('approve')
                                  return
                                }
                              } catch {
                                /* non-blocking */
                              }
                            }
                            setPhase('approve')
                          })()
                        }}
                      >
                        <Icon name="send" size={16} /> {t('compose.send')}
                      </button>
                      <button
                        type="button"
                        className="mail-tbtn"
                        disabled={busy}
                        onClick={() => {
                          void (async () => {
                            setError(null)
                            setNote(null)
                            setBusy(true)
                            try {
                              const payload = {
                                account_id: fromId || null,
                                to_addrs: to,
                                cc_addrs: showCc ? cc : '',
                                subject,
                                body_text: body,
                                in_reply_to_message_id: replyMsgId,
                                in_reply_to_header: inReplyHeader,
                                references_header: references,
                              }
                              const saved = draftId
                                ? await updateDraft(draftId, payload)
                                : await createDraft(payload)
                              setDraftId(saved.id)
                              setNote(t('compose.draftSaved'))
                              onDraftSaved?.()
                            } catch (e) {
                              setError(e instanceof Error ? e.message : t('compose.draftSaveFailed'))
                            } finally {
                              setBusy(false)
                            }
                          })()
                        }}
                      >
                        <Icon name="draft" size={16} /> {t('compose.draft')}
                      </button>
                      <span style={{ flex: 1 }} />
                      <span className="mail-set-note" style={{ margin: 0 }}>
                        {t('compose.undoHint')}
                      </span>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
